import { BehaviorSubject, Observable } from 'rxjs';
import { DocumentTypeEnum } from '../_enums/document-type.enum';
import { inject, Injectable } from '@angular/core';
import { WorkflowService } from './workflow.service';

@Injectable({
  providedIn: 'root',
})
export class DocumentService {
  private documents = new Map<DocumentTypeEnum, BehaviorSubject<File | undefined>>();
  // The template a generated document was made from, e.g. "Ondersteuningsplan JW".
  // The editor shows it as the title and the file names (download and Suites) use it.
  private titles = new Map<DocumentTypeEnum, string | undefined>();
  private documentChanged = new Map<DocumentTypeEnum, BehaviorSubject<boolean>>();
  private optionalDocuments = new BehaviorSubject<File[]>([]);
  private optionalDocumentsChanged = new BehaviorSubject<boolean>(false);
  private readonly workflowService = inject(WorkflowService);

  constructor() {
    Object.values(DocumentTypeEnum).forEach((type) => {
      this.documents.set(type, new BehaviorSubject<File | undefined>(undefined));
      this.documentChanged.set(type, new BehaviorSubject<boolean>(false));
    });
  }

  // ---------------- Required documents ----------------

  /**
   * `title` names the template a generated document was made from. A document the
   * user uploads has none: it is not known what it is.
   */
  public setDocument(type: DocumentTypeEnum, file: File, title?: string): void {
    const document = this.documents.get(type);
    if (document) {
      this.titles.set(type, title);
      document.next(file);
      this.workflowService.setStepInProgress(true);
    }
  }

  public getDocumentSnapshot(type: DocumentTypeEnum): File | undefined {
    return this.documents.get(type)?.getValue();
  }

  public getDocumentTitle(type: DocumentTypeEnum): string | undefined {
    return this.titles.get(type);
  }

  public deleteDocument(type: DocumentTypeEnum): void {
    const document = this.documents.get(type);
    if (document) {
      this.titles.delete(type);
      document.next(undefined);
    }
    this.documentChanged.get(type)?.next(true);
  }

  public getDocument(type: DocumentTypeEnum): Observable<File | undefined> {
    return this.documents.get(type)!.asObservable();
  }

  public isDocumentChanged(type: DocumentTypeEnum): Observable<boolean> {
    return this.documentChanged.get(type)!.asObservable();
  }

  public setDocumentChanged(type: DocumentTypeEnum, changed: boolean): void {
    const documentChanged = this.documentChanged.get(type);
    if (documentChanged) {
      documentChanged.next(changed);
    }
  }

  // An edited version of the same document: it keeps its title.
  public replaceDocument(type: DocumentTypeEnum, file: File): void {
    this.setDocument(type, file, this.titles.get(type));
    this.documentChanged.get(type)!.next(true);
  }

  // ---------------- Optional documents ----------------

  public getOptionalDocuments(): Observable<File[]> {
    return this.optionalDocuments.asObservable();
  }

  public getOptionalDocumentsValue(): File[] {
    return this.optionalDocuments.getValue();
  }

  public addOptionalDocument(file: File): void {
    this.optionalDocuments.next([...this.optionalDocuments.getValue(), file]);
    this.optionalDocumentsChanged.next(true);
    this.workflowService.setStepInProgress(true);
  }

  public removeOptionalDocument(index: number): void {
    const current = this.optionalDocuments.getValue();
    this.optionalDocuments.next(current.filter((_, i) => i !== index));
    this.optionalDocumentsChanged.next(true);
  }

  public removeAllOptionalDocuments(): void {
    this.optionalDocuments.next([]);
    this.optionalDocumentsChanged.next(true);
  }

  public clearOptionalDocuments(): void {
    this.optionalDocuments.next([]);
  }

  public isOptionalDocumentsChanged(): Observable<boolean> {
    return this.optionalDocumentsChanged.asObservable();
  }

  public setOptionalDocumentsChanged(changed: boolean): void {
    this.optionalDocumentsChanged.next(changed);
  }
}
